// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { enablePostalCodeLocationFinder } from "@bg/App/Store/Selectors/PostalCodeSelector";
import { constants } from "@bg/XDL/constants";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { getLocalStorage, hasLocalStorage } from "@bg/XDL/Utilities/localStorage";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    isProductListPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    stockOnly?: boolean;
    hasAlternativeWarehouses?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    showInventory: state.context.showInventory,
    // Xng-Change
    isAuth: getSession(state).isAuthenticated,
    enablePostalCodeLocationFinder: enablePostalCodeLocationFinder(state),
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: InjectableCss;
    messageGridItem?: InjectableCss;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    nonStockText?: TypographyPresentationProps;
    warehouseModelIcon?: IconPresentationProps;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        css: css`
            display: flex;
            align-items: flex-start;
        `,
    },
    availabilityByWarehouseLink: {
        css: css`
            line-height: 0;
            margin-right: 5px;
            margin-top: 3px;
            span {
                display: flex;
                align-items: center;
            }
        `,
    },
    messageGridItem: {
        css: css`
            width: 100%;
        `,
    },
    availabilityByWarehouseModal: {
        size: 500,
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        weight: fontConfig.weight.semiBold,
        css: css`
            display: flex;
            flex-wrap: wrap;
        `,
    },
    realTimeText: {
        weight: fontConfig.weight.bold,
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        lineHeight: "18px",
        weight: fontConfig.weight.semiBold,
        size: fontConfig.fontSize.xSmall,
    },
    availabilityByWarehouseTableHeader: {
        css: css`
            text-transform: uppercase;
            position: static !important;
        `,
    },
    availabilityByWarehouseTableCell: {
        css: css`
            border-bottom-color: ${getColor("common.ui3")};
            position: static !important;
        `,
    },
    nonStockText: {
        lineHeight: "18px",
        weight: fontConfig.weight.semiBold,
        size: fontConfig.fontSize.xSmall,
        color: "danger.main",
        css: css`
            max-width: 120px;
            display: block;
        `,
        ellipsis: true,
    },
    warehouseModelIcon: {
        src: "Info",
        color: "secondary.main",
        size: 13,
        css: css`
            margin-right: 5px;
        `,
    },
};

const InventoryMessage = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    font-size: ${fontConfig.fontSize.xSmall}px;
    color: ${getColor("common.ui2")};
    &,
    span {
        line-height: 18px;
    }

    span.in-stock {
        text-transform: uppercase;
        font-weight: ${fontConfig.weight.semiBold};
        color: ${getColor("success.main")};
    }

    span.out-of-stock,
    span.out-all {
        text-transform: uppercase;
        font-weight: ${fontConfig.weight.semiBold};
        color: ${getColor("danger.main")};
    }

    span.low-stock {
        text-transform: uppercase;
        font-weight: ${fontConfig.weight.semiBold};
        color: ${getColor("warning.main")};
    }

    span.dc {
        color: ${getColor("text.main")};
    }

    span.separator {
        margin: 0 10px;
    }

    &&&.stock-only {
        .separator,
        .dc {
            display: none;
        }

        .out-of-stock,
        .low-stock,
        .in-stock {
            margin-right: 0;
        }
    }

    ${IconWrapper} {
        margin-right: 5px;
        opacity: 0.9;
    }
`;

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
    isProductListPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage || !!isProductListPage;
};

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    isProductListPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    isAuth,
    stockOnly,
    enablePostalCodeLocationFinder,
    hasAlternativeWarehouses,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showAvailability, setShowAvailability] = React.useState(false);

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    // <xng-change>
    React.useEffect(() => {
        if (enablePostalCodeLocationFinder && !hasLocalStorage(constants.localStorage.POSTAL_CODE_ID) && !isAuth) {
            setShowAvailability(false);
        } else {
            setShowAvailability(true);
        }
    }, []);

    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));
    if (!showInventory) {
        return null;
    }

    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText}>{siteMessage("RealTimeInventory_InventoryLoadFailed")}</Typography>
            </StyledWrapper>
        );
    }

    if (!showAvailability) {
        return <></>;
    }

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {
        if (!isProductDetailsPage) {
            inventoryTextComponent = (
                <InventoryMessage {...styles.inventoryMessage} className={stockOnly ? "stock-only" : ""}>
                    <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                        {parse(availability.message.split("|")[0])}
                    </Typography>
                </InventoryMessage>
            );
        } else {
            inventoryTextComponent = (
                <InventoryMessage {...styles.inventoryMessage} className={stockOnly ? "stock-only" : ""}>
                    <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                        {parse(availability.message)}
                    </Typography>
                </InventoryMessage>
            );
        }
    } else if (
        availability?.messageType === AvailabilityMessageType.NoMessage &&
        availability?.message?.length &&
        availability?.message.includes("nonstock")
    ) {
        // non stock item
        inventoryTextComponent = (
            <StyledWrapper>
                <Typography {...styles.nonStockText}>{siteMessage("Custom_PriceInventory_NonStockProduct")}</Typography>
            </StyledWrapper>
        );
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    return (
        <StyledWrapper {...styles.container}>
            {showLink(availability, trackInventory, productSettings, isProductDetailsPage, isProductListPage) && (
                <>
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        onClick={viewAvailabilityByWarehouseClickHandler}
                        data-test-selector="availabilityByWarehouseLink"
                    >
                        {hasAlternativeWarehouses && <Icon {...styles.warehouseModelIcon} />}
                        <StyledWrapper {...styles.messageGridItem}>{inventoryTextComponent}</StyledWrapper>
                    </Link>
                    <Modal
                        {...styles.availabilityByWarehouseModal}
                        headline={translate("Branch Availability")}
                        isOpen={modalIsOpen}
                        size={650}
                        handleClose={modalCloseHandler}
                    >
                        {isLoading && (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        )}
                        {!isLoading && (
                            <>
                                {errorMessage ? (
                                    <Typography {...styles.nonStockText}>{errorMessage}</Typography>
                                ) : (
                                    <>
                                        <Typography lineHeight="20px" variant="p">
                                            {siteMessage("Custom_BranchAvailabilityModalMsg")}
                                        </Typography>
                                        <StyledWrapper
                                            css={css`
                                                overflow: auto;
                                            `}
                                        >
                                            <DataTable
                                                {...styles.availabilityByWarehouseTable}
                                                data-test-selector="availabilityByWarehouseModal"
                                            >
                                                <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                                    <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                        {translate("Branch")}
                                                    </DataTableHeader>
                                                    <DataTableHeader
                                                        css={css`
                                                            ${styles.availabilityByWarehouseTableHeader?.css}
                                                            text-align: right;
                                                            width: 130px;
                                                        `}
                                                    >
                                                        {translate("Availability")}
                                                    </DataTableHeader>
                                                </DataTableHead>
                                                <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                                    {warehouses?.map(warehouse => (
                                                        <DataTableRow
                                                            {...styles.availabilityByWarehouseTableRow}
                                                            key={warehouse.name}
                                                        >
                                                            <DataTableCell
                                                                css={css`
                                                                    ${styles.availabilityByWarehouseTableCell?.css}
                                                                    ${(warehouse as any).properties
                                                                        .isSelectedWarehouse === "true" &&
                                                                    `font-weight: ${fontConfig.weight.bold};`}
                                                                `}
                                                                data-test-selector="warehouseAvailabilityModal_Description"
                                                            >
                                                                {warehouse.description || warehouse.name}
                                                                {(warehouse as any).properties.isSelectedWarehouse ===
                                                                    "true" && <>&nbsp;{translate("(Your Branch)")}</>}
                                                            </DataTableCell>
                                                            <DataTableCell
                                                                css={css`
                                                                    ${styles.availabilityByWarehouseTableCell?.css}
                                                                    text-align: center;
                                                                    width: 130px;
                                                                    ${(warehouse as any).properties
                                                                        .isSelectedWarehouse === "true" &&
                                                                    `font-weight: ${fontConfig.weight.bold};`}
                                                                `}
                                                                data-test-selector="warehouseAvailabilityModal_Quantity"
                                                            >
                                                                {warehouse.qty}
                                                            </DataTableCell>
                                                        </DataTableRow>
                                                    ))}
                                                </DataTableBody>
                                            </DataTable>
                                        </StyledWrapper>
                                    </>
                                )}
                            </>
                        )}
                    </Modal>
                </>
            )}
            {!isProductDetailsPage && !isProductListPage && (
                <StyledWrapper {...styles.messageGridItem}>{inventoryTextComponent}</StyledWrapper>
            )}
        </StyledWrapper>
    );
};
export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
